import { CSSProperties, Children, HTMLAttributes, JSX, ReactNode, Ref } from 'react';
import styled from 'styled-components';

import { space } from '@creditornot/alchemy-react/tokens';

import { VerticalSeparator } from './VerticalSeparator';
import { breakpoints, media } from '../../media';

// Removes (string & {}) from union types
type LiteralUnion<T> = T extends string ? (string extends T ? never : T) : never;

type Breakpoint = 'xs' | keyof typeof breakpoints;
type ResponsiveProp<T> = Partial<Record<Breakpoint, T>>;

type AlignItems = LiteralUnion<CSSProperties['alignItems']>;
type Direction = LiteralUnion<CSSProperties['flexDirection']>;
type Wrap = LiteralUnion<CSSProperties['flexWrap']>;
type JustifyContent = LiteralUnion<CSSProperties['justifyContent']>;
type Gap = keyof typeof space;

type Props = {
  alignItems?: AlignItems | ResponsiveProp<AlignItems>;
  children: ReactNode;
  className?: string;
  columnGap?: Gap | ResponsiveProp<Gap>;
  direction?: Direction | ResponsiveProp<Direction>;
  dividers?: boolean;
  /**
   * `narrowXsmall` 2px,
   * `narrowSmall` 4px,
   * `narrowMedium` 6px,
   * `regularMedium` 8px,
   * `regularLarge` 12px,
   * `regularXlarge` 16px,
   * `regularXxlarge` 20px,
   * `regularXxxlarge` 24px,
   * `broadMedium` 28px,
   * `broadLarge` 32px,
   * `broadXlarge` 36px,
   * `broadXxlarge` 40px,
   * `broadXxxlarge` 48px,
   * `wideMedium` 56px,
   * `wideLarge` 64px,
   * `wideXlarge` 84px,
   * `wideXxlarge` 108px,
   * `wideXxxlarge` 140px
   */
  gap?: Gap | ResponsiveProp<Gap>;
  id?: string;
  justifyContent?: JustifyContent | ResponsiveProp<JustifyContent>;
  /**
   * @deprecated Stack is not an interactive element
   */
  onClick?: HTMLAttributes<HTMLDivElement>['onClick'];
  onMouseLeave?: HTMLAttributes<HTMLDivElement>['onMouseLeave'];
  ref?: Ref<HTMLDivElement>;
  role?: HTMLAttributes<HTMLDivElement>['role'];
  rowGap?: Gap | ResponsiveProp<Gap>;
  style?: CSSProperties;
  tag?: keyof JSX.IntrinsicElements;
  wrap?: Wrap | ResponsiveProp<Wrap>;
};

const FlexContainer = styled.div`
  --align-items: initial;
  --align-items-sm: initial;
  --align-items-md: initial;
  --align-items-lg: initial;
  --align-items-xl: initial;
  --column-gap: initial;
  --column-gap-sm: initial;
  --column-gap-md: initial;
  --column-gap-lg: initial;
  --column-gap-xl: initial;
  --direction: initial;
  --direction-sm: initial;
  --direction-md: initial;
  --direction-lg: initial;
  --direction-xl: initial;
  --justify-content: initial;
  --justify-content-sm: initial;
  --justify-content-md: initial;
  --justify-content-lg: initial;
  --justify-content-xl: initial;
  --row-gap: initial;
  --row-gap-sm: initial;
  --row-gap-md: initial;
  --row-gap-lg: initial;
  --row-gap-xl: initial;
  --wrap: initial;
  --wrap-sm: initial;
  --wrap-md: initial;
  --wrap-lg: initial;
  --wrap-xl: initial;

  display: flex;
  margin: 0;
  padding: 0;
  list-style-type: none;

  &:empty {
    display: none;
  }

  align-items: var(--align-items);
  column-gap: var(--column-gap);
  flex-direction: var(--direction);
  flex-wrap: var(--wrap);
  justify-content: var(--justify-content);
  row-gap: var(--row-gap);

  @media ${media.sm} {
    align-items: var(--align-items-sm, var(--align-items));
    column-gap: var(--column-gap-sm, var(--column-gap));
    flex-direction: var(--direction-sm, var(--direction));
    flex-wrap: var(--wrap-sm, var(--wrap));
    justify-content: var(--justify-content-sm, var(--justify-content));
    row-gap: var(--row-gap-sm, var(--row-gap));
  }

  @media ${media.md} {
    align-items: var(--align-items-md, var(--align-items-sm, var(--align-items)));
    column-gap: var(--column-gap-md, var(--column-gap-sm, var(--column-gap)));
    flex-direction: var(--direction-md, var(--direction-sm, var(--direction)));
    flex-wrap: var(--wrap-md, var(--wrap-sm, var(--wrap)));
    justify-content: var(--justify-content-md, var(--justify-content-sm, var(--justify-content)));
    row-gap: var(--row-gap-md, var(--row-gap-sm, var(--row-gap)));
  }

  @media ${media.lg} {
    align-items: var(
      --align-items-lg,
      var(--align-items-md, var(--align-items-sm, var(--align-items)))
    );
    column-gap: var(--column-gap-lg, var(--column-gap-md, var(--column-gap-sm, var(--column-gap))));
    flex-direction: var(--direction-lg, var(--direction-md, var(--direction-sm, var(--direction))));
    flex-wrap: var(--wrap-lg, var(--wrap-md, var(--wrap-sm, var(--wrap))));
    justify-content: var(
      --justify-content-lg,
      var(--justify-content-md, var(--justify-content-sm, var(--justify-content)))
    );
    row-gap: var(--row-gap-lg, var(--row-gap-md, var(--row-gap-sm, var(--row-gap))));
  }

  @media ${media.xl} {
    align-items: var(
      --align-items-xl,
      var(--align-items-lg, var(--align-items-md, var(--align-items-sm, var(--align-items))))
    );
    column-gap: var(
      --column-gap-xl,
      var(--column-gap-lg, var(--column-gap-md, var(--column-gap-sm, var(--column-gap))))
    );
    flex-direction: var(
      --direction-xl,
      var(--direction-lg, var(--direction-md, var(--direction-sm, var(--direction))))
    );
    flex-wrap: var(--wrap-xl, var(--wrap-lg, var(--wrap-md, var(--wrap-sm, var(--wrap)))));
    justify-content: var(
      --justify-content-xl,
      var(
        --justify-content-lg,
        var(--justify-content-md, var(--justify-content-sm, var(--justify-content)))
      )
    );
    row-gap: var(
      --row-gap-xl,
      var(--row-gap-lg, var(--row-gap-md, var(--row-gap-sm, var(--row-gap))))
    );
  }
`;

function getResponsiveValue(
  breakpoint: Breakpoint,
  value: ResponsiveProp<string>,
  lookup?: Record<string, string>,
) {
  const breakpointValue = value[breakpoint];

  if (!breakpointValue) {
    return;
  }

  return lookup ? lookup[breakpointValue] : breakpointValue;
}

function generateResponsiveStyle(
  name: string,
  value: string | ResponsiveProp<string>,
  lookup?: Record<string, string>,
) {
  if (typeof value === 'string') {
    return {
      [`--${name}`]: lookup ? lookup[value] : value,
    };
  }

  const breakpoints: Breakpoint[] = ['xs', 'sm', 'md', 'lg', 'xl'];
  return Object.fromEntries(
    breakpoints.map((breakpoint) => [
      breakpoint === 'xs' ? `--${name}` : `--${name}-${breakpoint}`,
      getResponsiveValue(breakpoint, value, lookup),
    ]),
  );
}

export function Stack({
  tag = 'div',
  alignItems,
  children,
  className,
  direction = 'column',
  dividers,
  gap,
  rowGap = gap,
  columnGap = gap,
  justifyContent,
  style,
  wrap,
  ...rest
}: Props) {
  const childrenCount = Children.count(children);

  const allStyles = {
    ...style,
    ...(alignItems && generateResponsiveStyle('align-items', alignItems)),
    ...(direction && generateResponsiveStyle('direction', direction)),
    ...(rowGap && generateResponsiveStyle('row-gap', rowGap, space)),
    ...(columnGap && generateResponsiveStyle('column-gap', columnGap, space)),
    ...(justifyContent && generateResponsiveStyle('justify-content', justifyContent)),
    ...(wrap && generateResponsiveStyle('wrap', wrap)),
  };

  return (
    <FlexContainer {...rest} as={tag} style={allStyles} className={className}>
      {Children.map(children, (child, index) => (
        <>
          {child}
          {dividers && index !== childrenCount - 1 ? <VerticalSeparator /> : null}
        </>
      ))}
    </FlexContainer>
  );
}
